import { useEffect, useRef, useState } from 'react';
import { AlertCircle, Banknote, Check, Clock, CreditCard, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { supabase } from '@/integrations/supabase/client';
import {
  isStillAwaitingPayment,
  useBookingPaymentStatus,
  useStartBookingCheckout,
  useSwitchBookingToCash,
  type BookingPaymentStatus,
} from '@/hooks/useBookingPayment';
import {
  clearPendingBookingPayment,
  formatHoldTime,
  isHoldExpired,
  savePendingBookingPayment,
  type PendingBookingPayment,
} from '@/lib/bookingPayment';

export type BookingPaymentReturnMode = 'success' | 'cancelled';

interface Props {
  mode: BookingPaymentReturnMode;
  bookingId: string;
  /** What this browser saved before going to Stripe (token, email, when). */
  pending: PendingBookingPayment | null;
  orgId: string | null;
  returnPath: string;
  currencySymbol: string;
  timeZone: string;
  /** Shown when we couldn't open Stripe in the first place. */
  openError?: string | null;
  onDone: () => void;
}

const money = (amount: number | null | undefined, symbol: string) => `${symbol}${(Number(amount) || 0).toFixed(2)}`;

function paidSentence(status: BookingPaymentStatus, symbol: string): string {
  const paid = Number(status.amountPaid) || 0;
  const total = Number(status.total) || 0;
  if (paid <= 0) return '';
  if (paid >= total) return `You've paid ${money(paid, symbol)} in full.`;
  return `Your ${money(paid, symbol)} deposit is paid, with ${money(total - paid, symbol)} to pay on the day.`;
}

/**
 * The booking page after Stripe Checkout (or when we couldn't get there):
 * says plainly what happened and what to do next.
 */
export function BookingPaymentReturn({
  mode,
  bookingId,
  pending,
  orgId,
  returnPath,
  currencySymbol,
  timeZone,
  openError,
  onDone,
}: Props) {
  const token = pending?.token ?? null;
  const statusQuery = useBookingPaymentStatus(bookingId, token, { poll: mode === 'success' });
  const status = statusQuery.data ?? null;
  const checkout = useStartBookingCheckout();
  const toCash = useSwitchBookingToCash();
  const [cashResult, setCashResult] = useState<'instant' | 'request' | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const accessLinkSent = useRef(Boolean(pending?.accessLinkSent));
  // Polling gives up after ~40s; after that we say it's still processing.
  const openedAt = useRef(Date.now());

  const settledPaid = Boolean(status && !isStillAwaitingPayment(status) && Number(status.amountPaid) > 0 && status.status !== 'cancelled');

  // Once it's paid and confirmed (or a request), send the private access link
  // the booking page normally sends straight after booking. Once per booking.
  useEffect(() => {
    if (!pending || !orgId || accessLinkSent.current) return;
    if (!(settledPaid || cashResult)) return;
    accessLinkSent.current = true;
    savePendingBookingPayment(window.sessionStorage, { ...pending, accessLinkSent: true });
    void supabase.functions
      .invoke('send-customer-magic-link', { body: { email: pending.email, organizationId: orgId } })
      .catch((error) => console.error('Customer access link failed:', error));
  }, [settledPaid, cashResult, pending, orgId]);

  const finish = () => {
    if (pending) clearPendingBookingPayment(window.sessionStorage, bookingId);
    onDone();
  };

  const retry = async () => {
    if (!token) return;
    setActionError(null);
    try {
      const { url } = await checkout.mutateAsync({ bookingId, token, returnPath });
      window.location.assign(url);
    } catch (error) {
      setActionError((error as Error).message);
      void statusQuery.refetch();
    }
  };

  const payCash = async () => {
    if (!token) return;
    setActionError(null);
    try {
      const result = await toCash.mutateAsync({ bookingId, token });
      setCashResult(result.confirmation);
      // Same emails a cash booking sends straight away.
      for (const fn of ['send-enquiry-confirmation', 'send-owner-notification']) {
        try {
          await supabase.functions.invoke(fn, { body: { recordId: bookingId, source: 'job', organizationId: orgId } });
        } catch (error) {
          console.error(`${fn} failed`, error);
        }
      }
    } catch (error) {
      setActionError((error as Error).message);
      void statusQuery.refetch();
    }
  };

  let icon = <Clock className="h-5 w-5 text-primary" />;
  let heading = '';
  let body = '';
  let actions: 'done' | 'retry' | 'pick-again' = 'done';

  const when = pending?.when ? ` ${pending.when}` : '';

  if (cashResult) {
    icon = cashResult === 'instant' ? <Check className="h-5 w-5 text-primary" /> : <Clock className="h-5 w-5 text-primary" />;
    heading = cashResult === 'instant' ? 'You’re booked in' : 'Request sent — we’ll confirm shortly';
    body = cashResult === 'instant'
      ? `We’ll see you${when}. You’ll pay cash on the day. We’ve emailed your confirmation.`
      : 'You’ll pay cash on the day. We’ll check the time works and confirm it by email shortly.';
  } else if (!token) {
    // Different browser/device, or storage was cleared: we can't look it up.
    heading = mode === 'success' ? 'Thanks, your payment went through' : 'Payment not finished';
    body = mode === 'success'
      ? 'We’ll email you to confirm your booking.'
      : 'You haven’t been charged. If you still want this clean, please pick a time and book again.';
    actions = mode === 'success' ? 'done' : 'pick-again';
  } else if (statusQuery.isLoading) {
    icon = <Loader2 className="h-5 w-5 animate-spin text-primary" />;
    heading = mode === 'success' ? 'Payment received' : 'Checking your booking';
    body = mode === 'success' ? 'We’re just confirming your booking. This only takes a moment.' : 'Just a moment…';
  } else if (status && settledPaid) {
    const paid = paidSentence(status, currencySymbol);
    if (status.status === 'pending_confirmation') {
      heading = 'Paid — request sent';
      body = `${paid} We’ll check the time works and confirm it shortly. If we can’t do it, you’ll get your money back automatically.`;
    } else {
      icon = <Check className="h-5 w-5 text-primary" />;
      heading = 'You’re booked in';
      body = `We’ll see you${when}. ${paid}${status.isRepeat ? ' Your regular cleans are booked in too.' : ''} We’ve emailed your confirmation and a private link to view your bookings.`;
    }
  } else if (mode === 'success' && status && isStillAwaitingPayment(status)) {
    icon = statusQuery.isFetching ? <Loader2 className="h-5 w-5 animate-spin text-primary" /> : <Clock className="h-5 w-5 text-primary" />;
    heading = 'Payment received';
    body = Date.now() - openedAt.current < 40_000
      ?'We’re just confirming your booking. This only takes a moment.'
      : 'Your payment is still being processed. We’ll email you as soon as your booking is confirmed.';
  } else if (status && isStillAwaitingPayment(status) && !isHoldExpired(status.holdExpiresAt)) {
    icon = <AlertCircle className="h-5 w-5 text-primary" />;
    heading = openError ? 'We couldn’t open the payment page' : 'Payment not finished';
    body = `You haven’t been charged. We’re holding your slot until ${formatHoldTime(status.holdExpiresAt, timeZone)}.${
      status.cashAllowed ? ' You can try again, or pay cash on the day instead.' : ' Pay by card to keep it.'
    }`;
    actions = 'retry';
  } else {
    icon = <AlertCircle className="h-5 w-5 text-primary" />;
    heading = 'That time is no longer held';
    body = 'You haven’t been charged. Sorry, we couldn’t hold that time any longer. Please pick a time and book again.';
    actions = 'pick-again';
  }

  const error = actionError ?? (actions === 'retry' ? openError : null);

  return (
    <Card
      className="tp-card max-w-3xl mx-auto mb-6"
      role="status"
      aria-label="Booking payment"
      data-payment-return={mode}
    >
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-xl">
          {icon}
          {heading}
        </CardTitle>
        <CardDescription>{body}</CardDescription>
        {error && <p className="text-sm text-destructive" role="alert">{error}</p>}
      </CardHeader>
      <CardContent className="flex flex-col gap-3 sm:flex-row sm:flex-wrap">
        {actions === 'retry' && (
          <>
            <Button type="button" variant="hero" size="lg" className="w-full sm:w-auto" onClick={retry} disabled={checkout.isPending || toCash.isPending}>
              {checkout.isPending ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <CreditCard className="mr-2 h-4 w-4" />}
              Try paying again
            </Button>
            {status?.cashAllowed && (
              <Button type="button" variant="outline" size="lg" className="w-full sm:w-auto" onClick={payCash} disabled={checkout.isPending || toCash.isPending}>
                {toCash.isPending ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Banknote className="mr-2 h-4 w-4" />}
                Pay cash on the day instead
              </Button>
            )}
            <Button type="button" variant="ghost" size="lg" className="w-full sm:w-auto" onClick={finish}>
              Choose another time
            </Button>
          </>
        )}
        {actions === 'pick-again' && (
          <Button type="button" variant="hero" size="lg" className="w-full sm:w-auto" onClick={finish}>
            Choose another time
          </Button>
        )}
        {actions === 'done' && !statusQuery.isLoading && (
          <Button type="button" variant="outline" size="lg" className="w-full sm:w-auto" onClick={finish}>
            Book another clean
          </Button>
        )}
      </CardContent>
    </Card>
  );
}
